import React, { useState, useEffect } from 'react';
import { AlertCircle, Calendar, Package, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Badge from '../ui/Badge';
import { supabase } from '../../lib/supabase';
import { format, isPast, differenceInDays } from 'date-fns';

interface WeOweItem {
  id: string;
  sales_order_id: string;
  manufactured_product_id: string;
  quantity: number;
  reason: string;
  promised_date: string | null;
  status: string;
  sales_order: {
    order_number: string;
    customer_name: string;
  };
  manufactured_product: {
    product_name: string;
  };
}

const WeOweWidget: React.FC = () => {
  const [weOweItems, setWeOweItems] = useState<WeOweItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWeOweItems();
  }, []);

  const loadWeOweItems = async () => {
    try {
      const { data, error } = await supabase
        .from('we_owe_items')
        .select(`
          id,
          sales_order_id,
          manufactured_product_id,
          quantity,
          reason,
          promised_date,
          status,
          sales_order:sales_orders(order_number, customer_name),
          manufactured_product:manufactured_products(product_name)
        `)
        .eq('status', 'pending')
        .order('promised_date', { ascending: true, nullsFirst: false })
        .limit(10);

      if (error) throw error;
      setWeOweItems(data || []);
    } catch (error) {
      console.error('Error loading We Owe items:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyBadge = (promisedDate: string | null) => {
    if (!promisedDate) {
      return <Badge variant="secondary">No Date Set</Badge>;
    }

    const date = new Date(promisedDate);
    const daysUntil = differenceInDays(date, new Date());

    if (isPast(date)) {
      return <Badge variant="error">Overdue</Badge>;
    } else if (daysUntil <= 3) {
      return <Badge variant="warning">Urgent</Badge>;
    } else if (daysUntil <= 7) {
      return <Badge variant="primary">Soon</Badge>;
    } else {
      return <Badge variant="success">On Track</Badge>;
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (weOweItems.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">We Owe Items</CardTitle>
            <div className="p-2 bg-green-50 rounded-full">
              <AlertCircle className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No pending We Owe items</p>
            <p className="text-sm">All customer commitments are fulfilled!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">We Owe Items</CardTitle>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-amber-50 rounded-full">
              <AlertCircle className="h-5 w-5 text-amber-600" />
            </div>
            <Link to="/sales-orders?filter=we-owe" className="text-sm text-blue-600 hover:underline">
              View All
            </Link>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {weOweItems.map((item) => (
            <Link
              key={item.id}
              to={`/sales-orders/${item.sales_order_id}`}
              className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">
                      {item.sales_order?.customer_name || 'Unknown Customer'}
                    </span>
                    <span className="text-sm text-gray-500">
                      #{item.sales_order?.order_number || 'N/A'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">
                    {item.manufactured_product?.product_name || 'Unknown Product'} × {item.quantity}
                  </p>
                </div>
                {getUrgencyBadge(item.promised_date)}
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  <span>{item.reason}</span>
                </div>
                {item.promised_date && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>Due: {format(new Date(item.promised_date), 'MMM d, yyyy')}</span>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default WeOweWidget;
